import styled from "styled-components";
import {
  device,
  colors,
  getTypographyFontStyle,
} from "../../styles/global.style";

export const ContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 4%;
  margin: 100px 16px 60px;
  border-radius: 16px;
  background: rgba(19, 88, 150, 0.03);

  @media only screen and ${device.md} {
    margin: 120px 32px 80px;
    padding: 48px 6%;
  }

  @media only screen and ${device.lg} {
    margin: 120px auto 80px;
    max-width: 1400px;
    padding: 56px 8%;
  }
`;

export const IntroText = styled.p`
  ${getTypographyFontStyle({ variant: "body1", weight: 400 })}
  color: ${colors.lightdark};
  margin: 1.5rem 0 2rem;
  max-width: 800px;
  line-height: 1.8;
  text-align: center;

  @media only screen and ${device.md} {
    text-align: center;
    margin: 2rem 0 3rem;
  }
`;

export const StatsSection = styled.div`
  width: 100%;
  margin: 2rem 0 3rem;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;

  @media only screen and ${device.md} {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`;

export const StatBox = styled.div`
  background: ${colors.white};
  padding: 24px 16px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(10, 22, 40, 0.06);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }

  .stat-number {
    font-family: "Space Grotesk", sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: ${colors.primary};
    margin-bottom: 8px;

    @media only screen and ${device.md} {
      font-size: 32px;
    }
  }

  .stat-label {
    font-family: "Inter", sans-serif;
    font-size: 13px;
    color: ${colors.darkGrey};

    @media only screen and ${device.md} {
      font-size: 14px;
    }
  }
`;

export const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 20px;
  width: 100%;
  margin: 2rem 0;

  @media only screen and ${device.sm} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and ${device.md} {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  @media only screen and ${device.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const ProjectCard = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: ${colors.white};
  box-shadow: 0 4px 16px rgba(10, 22, 40, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  aspect-ratio: 4 / 3;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(10, 22, 40, 0.12);

    .project-overlay {
      opacity: 1;
      visibility: visible;
    }

    img {
      transform: scale(1.05);
    }
  }
`;

export const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

export const ProjectOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to top,
    rgba(10, 22, 40, 0.95) 0%,
    rgba(10, 22, 40, 0.7) 50%,
    rgba(10, 22, 40, 0.3) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  &.project-overlay {
    opacity: 0;
    visibility: hidden;
  }

  ${ProjectCard}:hover & {
    opacity: 1;
    visibility: visible;
  }

  .category {
    display: inline-block;
    background: ${colors.primary};
    color: ${colors.white};
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 4px 10px;
    border-radius: 4px;
    margin-bottom: 8px;
    width: fit-content;
  }

  .view-hint {
    display: block;
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    margin-top: 8px;
    font-style: italic;
  }
`;

export const ProjectTitle = styled.h3`
  font-family: "Space Grotesk", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.white};
  margin: 0 0 4px 0;

  @media only screen and ${device.md} {
    font-size: 20px;
  }
`;

export const ProjectDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.5;

  @media only screen and ${device.md} {
    font-size: 14px;
  }
`;

export const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  background: rgba(245, 166, 35, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);

  svg {
    width: 28px;
    height: 28px;
    color: ${colors.white};
    margin-left: 4px;
  }
`;

export const VideoCard = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(10, 22, 40, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(10, 22, 40, 0.12);
  }

  .video-info {
    padding: 20px;
  }

  ${ProjectTitle} {
    color: ${colors.secondary};
    margin-bottom: 8px;
  }

  ${ProjectDescription} {
    color: ${colors.darkGrey};
  }
`;

export const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${colors.secondary};
  overflow: hidden;
  cursor: pointer;

  &:hover ${PlayButton} {
    transform: translate(-50%, -50%) scale(1.1);
    background: ${colors.primary};
  }
`;

export const VideoThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${VideoContainer}:hover & {
    transform: scale(1.05);
  }
`;
